import { fetchPlaceTypes, fetchSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan, deleteSubscriptionPlan, ApiError, } from '../../../api/adminApi';
import { t, createField, createScreenHeader, createBackButton, isUnauthorized, isNotFound } from './context';
export function showSubscriptionsScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.subscriptions.title'));
    const content = document.createElement('div');
    content.className = 'admin__content';
    content.textContent = t('admin.subscriptions.loading');
    const backButton = createBackButton(ctx, t('admin.subscriptions.back'));
    ctx.screen.append(header, content, backButton);
    Promise.all([fetchSubscriptionPlans(), fetchPlaceTypes()])
        .then(([plans, placeTypes]) => {
        renderSubscriptionsContent(ctx, content, plans, placeTypes);
    })
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load subscription plans:', err);
        content.textContent = t('admin.subscriptions.loadError');
    });
}
// Only the plan list is re-fetched after a create/update/delete — place types
// don't change as a side effect of plan CRUD.
function refreshSubscriptionsTable(ctx, content, placeTypes) {
    fetchSubscriptionPlans()
        .then((plans) => renderSubscriptionsContent(ctx, content, plans, placeTypes))
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to reload subscription plans:', err);
        content.textContent = t('admin.subscriptions.loadError');
    });
}
function renderSubscriptionsContent(ctx, content, plans, placeTypes) {
    content.innerHTML = '';
    if (plans.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'admin__table-empty';
        empty.textContent = t('admin.subscriptions.empty');
        content.appendChild(empty);
    }
    else {
        const table = document.createElement('table');
        table.className = 'admin__table';
        const thead = document.createElement('thead');
        const headRow = document.createElement('tr');
        const headLabels = [
            t('admin.subscriptions.table.name'),
            t('admin.subscriptions.table.hours'),
            t('admin.subscriptions.table.price'),
            t('admin.subscriptions.table.active'),
            '',
        ];
        for (const label of headLabels) {
            const th = document.createElement('th');
            th.textContent = label;
            headRow.appendChild(th);
        }
        thead.appendChild(headRow);
        table.appendChild(thead);
        const tbody = document.createElement('tbody');
        for (const plan of plans) {
            const row = document.createElement('tr');
            const nameCell = document.createElement('td');
            nameCell.textContent = plan.name;
            const hoursCell = document.createElement('td');
            hoursCell.textContent = String(plan.hours);
            const priceCell = document.createElement('td');
            priceCell.textContent = `${plan.price.toFixed(2)} €`;
            const activeCell = document.createElement('td');
            activeCell.textContent = plan.isActive
                ? t('admin.subscriptions.table.activeYes')
                : t('admin.subscriptions.table.activeNo');
            const actionsCell = document.createElement('td');
            const editButton = document.createElement('button');
            editButton.type = 'button';
            editButton.className = 'admin__table-edit';
            editButton.textContent = t('admin.subscriptions.table.edit');
            editButton.addEventListener('click', () => openPlanModal(ctx, plan, placeTypes, content));
            actionsCell.appendChild(editButton);
            row.append(nameCell, hoursCell, priceCell, activeCell, actionsCell);
            tbody.appendChild(row);
        }
        table.appendChild(tbody);
        content.appendChild(table);
    }
    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'cta admin__add';
    addButton.textContent = t('admin.subscriptions.add');
    addButton.addEventListener('click', () => openPlanModal(ctx, null, placeTypes, content));
    content.appendChild(addButton);
}
function openDeleteConfirm(ctx, plan, planOverlay, content, placeTypes) {
    const confirmOverlay = document.createElement('div');
    confirmOverlay.className = 'admin__modal-overlay';
    confirmOverlay.addEventListener('click', (event) => {
        if (event.target === confirmOverlay)
            confirmOverlay.remove();
    });
    const confirmModal = document.createElement('div');
    confirmModal.className = 'admin__modal admin__modal--confirm';
    const text = document.createElement('p');
    text.className = 'admin__modal-confirm-text';
    text.textContent = t('admin.planModal.deleteConfirmText');
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta admin__submit';
    noButton.textContent = t('admin.planModal.deleteConfirmNo');
    noButton.addEventListener('click', () => confirmOverlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = 'cta admin__delete';
    yesButton.textContent = t('admin.planModal.deleteConfirmYes');
    yesButton.addEventListener('click', () => {
        error.hidden = true;
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        deleteSubscriptionPlan(plan.id)
            .then(() => {
            confirmOverlay.remove();
            planOverlay.remove();
            refreshSubscriptionsTable(ctx, content, placeTypes);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                confirmOverlay.remove();
                planOverlay.remove();
                ctx.showLogin();
                return;
            }
            if (isNotFound(err)) {
                // Already gone (e.g. deleted from another tab/admin) — reconcile
                // the UI with reality instead of reporting a delete failure.
                confirmOverlay.remove();
                planOverlay.remove();
                refreshSubscriptionsTable(ctx, content, placeTypes);
                return;
            }
            const message = err instanceof ApiError && err.status === 409
                ? t('admin.planModal.deleteInUseError')
                : t('admin.planModal.deleteError');
            error.textContent = message;
            error.hidden = false;
            yesButton.disabled = false;
            noButton.disabled = false;
            yesButton.classList.remove('is-loading');
        });
    });
    actions.append(noButton, yesButton);
    confirmModal.append(text, error, actions);
    confirmOverlay.appendChild(confirmModal);
    ctx.section.appendChild(confirmOverlay);
}
function openPlanModal(ctx, plan, placeTypes, content) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'admin__modal';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'admin__modal-close';
    closeButton.textContent = '×';
    closeButton.setAttribute('aria-label', t('admin.planModal.closeLabel'));
    closeButton.addEventListener('click', () => overlay.remove());
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = plan ? t('admin.planModal.editTitle') : t('admin.planModal.createTitle');
    const form = document.createElement('form');
    form.className = 'admin__form';
    const nameField = createField(t('admin.planModal.nameLabel'), 'text', plan?.name ?? '');
    const hoursField = createField(t('admin.planModal.hoursLabel'), 'number', plan ? String(plan.hours) : '');
    hoursField.input.min = '1';
    hoursField.input.step = '1';
    const priceField = createField(t('admin.planModal.priceLabel'), 'number', plan ? String(plan.price) : '');
    priceField.input.min = '0';
    priceField.input.step = '0.01';
    const validDaysField = createField(t('admin.planModal.validDaysLabel'), 'number', plan ? String(plan.validDays) : '');
    validDaysField.input.min = '1';
    validDaysField.input.step = '1';
    const placeTypeWrapper = document.createElement('label');
    placeTypeWrapper.className = 'admin__field';
    const placeTypeLabel = document.createElement('span');
    placeTypeLabel.className = 'admin__field-label';
    placeTypeLabel.textContent = t('admin.planModal.placeTypeLabel');
    const placeTypeSelect = document.createElement('select');
    placeTypeSelect.className = 'admin__input';
    placeTypeSelect.required = true;
    for (const placeType of placeTypes) {
        const option = document.createElement('option');
        option.value = String(placeType.id);
        option.textContent = placeType.name;
        if (plan && plan.placeTypeId === placeType.id)
            option.selected = true;
        placeTypeSelect.appendChild(option);
    }
    placeTypeWrapper.append(placeTypeLabel, placeTypeSelect);
    const activeWrapper = document.createElement('label');
    activeWrapper.className = 'admin__field admin__field--checkbox';
    const activeCheckbox = document.createElement('input');
    activeCheckbox.type = 'checkbox';
    activeCheckbox.checked = plan ? plan.isActive : true;
    const activeLabel = document.createElement('span');
    activeLabel.textContent = t('admin.planModal.activeLabel');
    activeWrapper.append(activeCheckbox, activeLabel);
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'cta admin__submit';
    submit.textContent = plan ? t('admin.planModal.saveLabel') : t('admin.planModal.createLabel');
    form.append(nameField.wrapper, hoursField.wrapper, priceField.wrapper, placeTypeWrapper, validDaysField.wrapper, activeWrapper, error, submit);
    if (plan) {
        const deleteButton = document.createElement('button');
        deleteButton.type = 'button';
        deleteButton.className = 'cta admin__delete';
        deleteButton.textContent = t('admin.planModal.delete');
        deleteButton.addEventListener('click', () => openDeleteConfirm(ctx, plan, overlay, content, placeTypes));
        form.appendChild(deleteButton);
    }
    form.addEventListener('submit', (event) => {
        event.preventDefault();
        error.hidden = true;
        const input = {
            name: nameField.input.value.trim(),
            placeTypeId: Number(placeTypeSelect.value),
            hours: Number(hoursField.input.value),
            price: Number(priceField.input.value),
            validDays: Number(validDaysField.input.value),
            isActive: activeCheckbox.checked,
        };
        const isValid = input.name.length > 0 &&
            Number.isInteger(input.placeTypeId) &&
            Number.isFinite(input.hours) &&
            input.hours > 0 &&
            Number.isFinite(input.price) &&
            input.price >= 0 &&
            Number.isFinite(input.validDays) &&
            input.validDays > 0;
        if (!isValid) {
            error.textContent = t('admin.planModal.validationError');
            error.hidden = false;
            return;
        }
        submit.disabled = true;
        submit.classList.add('is-loading');
        const request = plan ? updateSubscriptionPlan(plan.id, input) : createSubscriptionPlan(input);
        request
            .then(() => {
            overlay.remove();
            refreshSubscriptionsTable(ctx, content, placeTypes);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            if (isNotFound(err)) {
                // The plan was deleted elsewhere while this modal was open —
                // reconcile the UI with reality instead of reporting a save failure.
                overlay.remove();
                refreshSubscriptionsTable(ctx, content, placeTypes);
                return;
            }
            error.textContent = t('admin.planModal.saveError');
            error.hidden = false;
            submit.disabled = false;
            submit.classList.remove('is-loading');
        });
    });
    modal.append(closeButton, title, form);
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
}
